import { useState, useEffect } from "react";
import { IoClose } from "react-icons/io5";
import { HiOutlineExclamationCircle, HiOutlinePaperClip, HiOutlineDownload } from "react-icons/hi";
import API from "../../api";
import { capture } from "../../instrument";

const EmailViewModal = ({ email, onClose }) => {
  const [htmlContent, setHtmlContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    const fetchEmailHtml = async () => {
      if (!email?._id) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await API.get(`/email/${email._id}`);
        if (response.ok === false) return setError(response.message);

        if (response.data?.attachments) setAttachments(response.data.attachments);
        if (response.data?.htmlUrl) {
          const htmlResponse = await fetch(response.data.htmlUrl);
          if (htmlResponse.ok) {
            const html = await htmlResponse.text();
            setHtmlContent(html);
          } else {
            setError("Impossible de charger le contenu de l'email");
          }
        }
      } catch (err) {
        console.error("Error fetching email HTML:", err);
        capture(err);
        setError("Erreur lors du chargement de l'email");
      } finally {
        setLoading(false);
      }
    };

    fetchEmailHtml();
  }, [email?._id]);

  if (!email) return null;

  const patientEmail = email.recipient || "";

  return (
    <div className="fixed inset-0 bg-black/50 z-[51] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-[90vw] max-w-[1000px] h-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-white">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Email envoyé</h2>
            <p className="text-sm text-gray-500">
              {new Date(email.createdAt).toLocaleDateString("fr-FR", {
                day: "2-digit",
                month: "long",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <IoClose className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Email Metadata */}
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-3 text-sm">
          <div className="space-y-1">
            <div className="flex">
              <span className="text-gray-400 w-14 flex-shrink-0">De</span>
              <span className="text-gray-700">{email.sender}</span>
            </div>
            <div className="flex">
              <span className="text-gray-400 w-14 flex-shrink-0">A</span>
              <span className="text-gray-700">{patientEmail}</span>
            </div>
            <div className="flex">
              <span className="text-gray-400 w-14 flex-shrink-0">Objet</span>
              <span className="text-gray-900 font-medium">{email.subject}</span>
            </div>
          </div>
        </div>

        {/* Attachments */}
        {attachments.length > 0 && (
          <div className="bg-white border-b border-gray-200 px-6 py-3">
            <div className="flex items-center gap-2 mb-2">
              <HiOutlinePaperClip className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-500">Pièces jointes ({attachments.length})</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {attachments.map((attachment, index) => (
                <a
                  key={index}
                  href={attachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 transition-colors group"
                >
                  <span className="truncate max-w-[200px]">{attachment.filename}</span>
                  <HiOutlineDownload className="w-4 h-4 text-gray-400 group-hover:text-gray-600 flex-shrink-0" />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Email Content */}
        <div className="flex-1 relative bg-gray-100 min-h-[400px]">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-gray-500">Chargement de l'email...</div>
            </div>
          )}

          {error && !loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6">
              <HiOutlineExclamationCircle className="w-12 h-12 text-amber-500" />
              <div className="text-gray-600 text-center">
                <p className="font-medium">{error}</p>
                <p className="text-sm text-gray-500 mt-1">L'apercu de l'email n'est pas disponible</p>
              </div>
            </div>
          )}

          {!loading && !error && htmlContent && <iframe srcDoc={htmlContent} title={email.subject} className="absolute inset-0 w-full h-full border-0" sandbox="" />}

          {!loading && !error && !htmlContent && !email.htmlKey && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6">
              <HiOutlineExclamationCircle className="w-12 h-12 text-gray-400" />
              <div className="text-gray-600 text-center">
                <p className="font-medium">Apercu non disponible</p>
                <p className="text-sm text-gray-500 mt-1">Le contenu HTML de cet email n'a pas ete enregistre</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailViewModal;
